/**
 * Domain vocabulary for the Amazon shift console.
 *
 * Every literal union here was derived by aggregating the live
 * `amazon_shift_new` cluster, not guessed. Counts in comments are the
 * observed distribution at time of writing and are there to show which
 * branches are hot — they are not constraints.
 */

/* ------------------------------------------------------------------ *
 * Customer
 * ------------------------------------------------------------------ */

/** `customers.status` — drives which list a customer appears in. */
export const CUSTOMER_STATUSES = ['pending', 'approved', 'confirmed'] as const
export type CustomerStatus = (typeof CUSTOMER_STATUSES)[number]

/**
 * The `agents.username` whose customers are triaged in their own rail queue.
 *
 * Telegram signups are a distinct intake, so they get a list of their own and
 * are kept out of the main Pending list. A literal rather than a lookup: the
 * `agents` collection is data this API only reads, and a queue in the
 * navigation is a product decision, not a row somebody can add by accident.
 *
 * It is also the customer-id prefix every such customer carries — `tg000001` —
 * because `customers.write.ts` builds the prefix from exactly this field.
 */
export const TELEGRAM_AGENT_USERNAME = 'tg'

/**
 * A customer list that is scoped to one agent queue instead of to a status.
 *
 * One member today. It exists as a union so the exclusion below stays a
 * question about "which segmented queues are there" rather than a `tg` literal
 * sprinkled through the table, the rail and the topbar.
 */
export const CUSTOMER_SEGMENTS = ['telegram'] as const
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number]

/** The agent username backing each segmented queue. */
export const SEGMENT_AGENT_USERNAME: Readonly<Record<CustomerSegment, string>> = Object.freeze({
  telegram: TELEGRAM_AGENT_USERNAME,
})

/** What each segmented queue is called on screen — nav item, title, prose. */
export const SEGMENT_LABEL: Readonly<Record<CustomerSegment, string>> = Object.freeze({
  telegram: 'Telegram',
})

/**
 * Which queue an agent username belongs to, keyed LOWER-CASE.
 *
 * The reverse of `SEGMENT_AGENT_USERNAME`, for the one question the create form
 * asks: the customer was just saved under this agent, so which list is it now
 * on? Derived rather than written out, so the two can never disagree.
 */
export const SEGMENT_BY_AGENT_USERNAME: Readonly<Record<string, CustomerSegment>> = Object.freeze(
  Object.fromEntries(
    (Object.entries(SEGMENT_AGENT_USERNAME) as [CustomerSegment, string][]).map(
      ([segment, username]) => [username.toLowerCase(), segment]
    )
  )
)

/**
 * Agents whose customers the main lists leave to their own queue.
 *
 * Derived from `SEGMENT_AGENT_USERNAME` so that adding a segment above cannot
 * leave its customers showing up in two places at once.
 */
export const SEGMENTED_AGENT_USERNAMES: readonly string[] = Object.freeze(
  Object.values(SEGMENT_AGENT_USERNAME)
)

/** `customers.mode` — stored as a *string* digit in Mongo. */
export const MODES = ['0', '1', '2', '3', '4', '5'] as const
export type Mode = (typeof MODES)[number]

/**
 * `customers.assessmentStatus` — where the customer's most recent audio
 * assessment run got to. `null` means they have never sat one.
 *
 * Written by `hiring/assessment/record-repo.ts` in this system and by the
 * sibling panel's runner onto the same documents; read by both consoles. Only
 * `'completed'` unlocks the questions hand-off in the customer drawer.
 */
export const CUSTOMER_ASSESSMENT_STATUSES = ['running', 'completed', 'failed', 'stopped'] as const
export type CustomerAssessmentStatus = (typeof CUSTOMER_ASSESSMENT_STATUSES)[number]

/**
 * `customers.sessionStatus` — denormalised copy of the live session state.
 * `null` means the customer has never been started.
 */
export const SESSION_STATUSES = ['idle', 'running', 'stopped', 'failed', 'expired'] as const
export type SessionStatus = (typeof SESSION_STATUSES)[number]

/**
 * `customers.work_hours` — a JSON array *inside a string*, e.g. '["Flex Time"]'.
 *
 * Spelled exactly as the collection stores them, inconsistencies included: two
 * are hyphenated and lower-cased after the hyphen, the third is two capitalised
 * words. Those are the only three values in the whole collection, and the
 * sibling system matches on them literally — "Full Time" would be a value
 * nothing downstream recognises, silently.
 */
export const WORK_HOURS = ['Full-time', 'Part-time', 'Flex Time'] as const
export type WorkHours = (typeof WORK_HOURS)[number]

/**
 * `customers.shift_priority` — a plain string, NOT JSON-in-a-string like the
 * three columns above.
 *
 * Introduced by the sibling agent panel (2026-09-02) and written by both
 * consoles since, so rows older than that simply lack the field and read as
 * `null`. Spelled exactly as that panel stores it: a value the other writer
 * would not recognise is a second convention in a shared column. Only
 * meaningful when `work_hours` includes `"Full-time"` — a night shift is not a
 * thing a flex worker picks — but collected and stored regardless, so the two
 * apps agree on what a complete record looks like.
 *
 * ── What reads it ───────────────────────────────────────────────────
 * The auto-picker, when it chooses a job and shift for a customer who has
 * neither: a FULL-TIME candidate is offered only if the hours in its
 * `scheduleText` fall in the half of the clock this names. `'Any'` gates
 * nothing, and part-time and flex candidates are never gated — see
 * `apps/api/src/hiring/shift-band.ts` for the reading and the operator-editable
 * day window it is read against.
 *
 * ABSENT MUST MEAN `'Any'`. Rows older than the field simply lack it, and a
 * missing preference that gated anything would stop every one of them from ever
 * matching — a regression on rows nobody touched.
 */
export const SHIFT_PRIORITIES = ['Any', 'Day Shift', 'Night Shift'] as const
export type ShiftPriority = (typeof SHIFT_PRIORITIES)[number]

/**
 * The Shift filter's vocabulary: the three priorities under URL-sized names,
 * plus `unset` for the rows that carry no priority at all.
 *
 * Short tokens rather than the stored strings, because these travel in the
 * address bar and the cache key — `?shift=day,night` is a link an operator can
 * read and type, `?shift=Day%20Shift` is not — and because the stored spelling
 * is the sibling panel's convention, which a URL should not be coupled to.
 *
 * `unset` is a member on purpose. Rows older than the column, and leads that
 * converted without one, read as `null`; an operator filling those in needs
 * to be able to list exactly them, and "not one of the three" is a question
 * the list can answer in one clause (see `buildShiftFilter`).
 */
export const SHIFT_FILTERS = ['any', 'day', 'night', 'unset'] as const
export type ShiftFilter = (typeof SHIFT_FILTERS)[number]

/** Each token that names a priority → the value the column stores for it. */
export const SHIFT_FILTER_PRIORITY: Readonly<
  Record<Exclude<ShiftFilter, 'unset'>, ShiftPriority>
> = Object.freeze({
  any: 'Any',
  day: 'Day Shift',
  night: 'Night Shift',
})

/**
 * `customers.address_state` — the full province name, not the two-letter code.
 *
 * Observed: Ontario 221, Alberta 142, British Columbia 22, then a long tail of
 * one. Two rows hold "ontario" and one holds "MB"; both are data entry, not a
 * second convention, so new rows use the canonical spelling.
 */
export const PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
] as const
export type Province = (typeof PROVINCES)[number]

/* ------------------------------------------------------------------ *
 * Session pipeline
 * ------------------------------------------------------------------ */

/**
 * `session_logs.stepKey`, ordered by real execution sequence.
 *
 * The order matters: the UI renders progress by taking the index of the
 * current step in this array. Steps that loop (`keep_alive_ping`,
 * `waf_recovery`) sit at the point in the pipeline where they run.
 */
export const PIPELINE_STEPS = [
  'customer_validation',
  'duplicate_session_check',
  'session_record_create',
  'worker_job_enqueue',
  'auth_context_initialize',
  'auth_context_collect',
  'sign_in_request',
  'otp_baseline_snapshot',
  'otp_wait',
  'otp_extract',
  'otp_confirm',
  'authorize_account',
  'create_application',
  'schedule_fetch',
  'update_application',
  'keep_alive_ping',
  'waf_recovery',
  'schedule_active_signal',
  'reauth',
  'session_summary',
  'stop_requested',
  'resource_cleanup',
] as const
export type PipelineStep = (typeof PIPELINE_STEPS)[number]

/** Index lookup for progress rendering. `-1` for unknown steps. */
export const STEP_INDEX: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(PIPELINE_STEPS.map((s, i) => [s, i]))
)

/**
 * Human labels. Mongo carries `stepLabel` alongside `stepKey`, but it is not
 * present on every document, so the UI falls back to this map.
 */
export const STEP_LABELS: Readonly<Record<PipelineStep, string>> = Object.freeze({
  customer_validation: 'Customer Validation',
  duplicate_session_check: 'Duplicate Session Check',
  session_record_create: 'Session Record Create',
  worker_job_enqueue: 'Worker Job Enqueue',
  auth_context_initialize: 'Auth Context Initialize',
  auth_context_collect: 'Auth Context Collect',
  sign_in_request: 'Sign-In Request',
  otp_baseline_snapshot: 'OTP Baseline Snapshot',
  otp_wait: 'OTP Wait',
  otp_extract: 'OTP Extract',
  otp_confirm: 'OTP Confirm',
  authorize_account: 'Authorize Account',
  create_application: 'Create Application',
  schedule_fetch: 'Schedule Fetch',
  update_application: 'Update Application',
  keep_alive_ping: 'Keep-Alive Ping',
  waf_recovery: 'WAF Recovery',
  schedule_active_signal: 'Schedule Active Signal',
  reauth: 'Re-authenticate',
  session_summary: 'Session Summary',
  stop_requested: 'Stop Requested',
  resource_cleanup: 'Resource Cleanup',
})

/**
 * Plain-English descriptions of what the automation is doing right now.
 *
 * `STEP_LABELS` above is the engineer's name for a step; this is the operator's.
 * The console shows these instead, because "Create Application 13/22" tells you
 * nothing unless you already know the pipeline, whereas "Creating application"
 * needs no explanation. Written as present participles so a row reads as a
 * sentence about what is happening.
 */
export const FRIENDLY_STEP_LABELS: Readonly<Record<PipelineStep, string>> = Object.freeze({
  customer_validation: 'Checking customer details',
  duplicate_session_check: 'Checking for a running session',
  session_record_create: 'Preparing the session',
  worker_job_enqueue: 'Queued for a worker',
  auth_context_initialize: 'Opening a browser',
  auth_context_collect: 'Preparing sign-in',
  sign_in_request: 'Signing in',
  otp_baseline_snapshot: 'Watching the inbox',
  otp_wait: 'Waiting for the email code',
  otp_extract: 'Reading the email code',
  otp_confirm: 'Confirming the code',
  authorize_account: 'Authorising the account',
  create_application: 'Creating the application',
  schedule_fetch: 'Looking up the schedule',
  update_application: 'Submitting the application',
  keep_alive_ping: 'Holding the session open',
  waf_recovery: 'Clearing a security check',
  schedule_active_signal: 'Schedule went live',
  reauth: 'Signing in again',
  session_summary: 'Wrapping up',
  stop_requested: 'Stopping',
  resource_cleanup: 'Cleaning up',
})

/** Friendly text for a step, falling back sensibly for unknown keys. */
export function friendlyStep(step: string | null | undefined): string {
  if (!step) return 'Not started'
  const known = FRIENDLY_STEP_LABELS[step as PipelineStep]
  if (known) return known
  // Unknown step from a newer worker: humanise the key rather than show snake_case.
  return step.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * The subset of steps that represent steady-state operation rather than
 * progress. Once a session reaches these it has "arrived" — the UI shows a
 * live pulse instead of a progress bar.
 */
export const STEADY_STATE_STEPS: readonly PipelineStep[] = [
  'keep_alive_ping',
  'waf_recovery',
  'schedule_active_signal',
]

/** `session_logs.level` */
export const LOG_LEVELS = ['info', 'success', 'warn', 'error'] as const
export type LogLevel = (typeof LOG_LEVELS)[number]

/** `session_logs.status` */
export const LOG_STATUSES = ['started', 'success', 'retrying', 'failed', 'stopped'] as const
export type LogStatus = (typeof LOG_STATUSES)[number]

/** `session_logs.category` */
export const LOG_CATEGORIES = [
  'session',
  'auth_context',
  'otp',
  'sign_in',
  'stop',
  'authorization',
  'worker',
] as const
export type LogCategory = (typeof LOG_CATEGORIES)[number]

/* ------------------------------------------------------------------ *
 * Schedule
 * ------------------------------------------------------------------ */

export const SCHEDULE_TYPES = ['FULL_TIME', 'PART_TIME', 'FLEX_TIME', 'REDUCED_TIME'] as const
export type ScheduleType = (typeof SCHEDULE_TYPES)[number]

/* ------------------------------------------------------------------ *
 * Derived display state
 * ------------------------------------------------------------------ */

/**
 * What the operator actually needs to see in the Session column, collapsed
 * from (sessionStatus × currentStep × heartbeat age) into one value.
 *
 * `stale` is the important one: a session whose status says `running` but
 * whose heartbeat has gone quiet is the failure mode that the current panel
 * cannot show, and it is the thing an operator most needs to catch.
 */
export const LIVE_STATES = [
  'never_started',
  'starting',
  'live',
  'stale',
  'failed',
  'stopped',
] as const
export type LiveState = (typeof LIVE_STATES)[number]

/** A heartbeat older than this while `running` means the worker is gone. */
export const HEARTBEAT_STALE_MS = 90_000
